import { Img } from 'openimg/react'
import { useRef } from 'react'
import { Link, Form } from 'react-router'
import { Button } from '#app/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from '#app/components/ui/dropdown-menu'
import { Icon, type IconName } from '#app/components/ui/icon'
import {
	useOptimisticThemeMode,
	ThemeSwitch,
} from '#app/routes/api+/resources+/theme-switch.tsx'
import { cn } from '#app/utils/misc'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { useUser } from '#app/utils/user.ts'

export function UserDropdown() {
	const user = useUser()
	const requestInfo = useRequestInfo()
	const currProjectId = requestInfo.userPrefs.projectId
	const formRef = useRef<HTMLFormElement>(null)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					className="flex items-center gap-0 p-2 text-white hover:bg-white/10 hover:text-white sm:gap-2"
				>
					<Img
						className="h-8 w-8 rounded-full object-cover"
						alt={user.name ?? user.username}
						src={getUserImgSrc(user.image?.objectKey)}
						width={256}
						height={256}
					/>
					<span className="hidden text-body-sm font-bold sm:block">
						{user.name ?? user.username}
					</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end" className="w-56">
					<DropdownMenuItem asChild>
						<WiggleIconLink
							to={`/project/${currProjectId}/dashboard`}
							iconName="dashboard"
							text="Dashboard"
						/>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<WiggleIconLink
							to={`/project/${currProjectId}/post-assistant`}
							iconName="pencil-1"
							text="New Post Assistant"
						/>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<WiggleIconLink
							to={`/settings/projects/${currProjectId}/instagram/profile`}
							iconName="id-card"
							text="Instagram Profile"
						/>
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem asChild>
						<WiggleIconLink
							to="/settings/user"
							iconName="gear"
							text="Settings"
						/>
					</DropdownMenuItem>
					<DropdownMenuItem>
						<ThemeToggle />
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<Form action="/logout" method="POST" ref={formRef}>
						<DropdownMenuItem asChild>
							<button
								type="submit"
								className="group w-full hover:bg-accent-foreground hover:text-white"
							>
								<Icon
									className="group-hover:animate-wiggle mr-2 text-body-md transition-transform"
									name="exit"
								>
									Logout
								</Icon>
							</button>
						</DropdownMenuItem>
					</Form>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

export function WiggleIconLink({
	to,
	iconName,
	text,
	className,
	...props
}: {
	to: string
	iconName: IconName
	text: string
} & React.ComponentPropsWithoutRef<typeof Link>) {
	return (
		<Link
			prefetch="intent"
			to={to}
			className={cn('group w-full', className)}
			{...props}
		>
			<Icon
				className="group-hover:animate-wiggle mr-2 text-body-md transition-transform"
				name={iconName}
			>
				{text}
			</Icon>
		</Link>
	)
}

function ThemeToggle() {
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? requestInfo.userPrefs.theme ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const modeIcon = {
		light: 'sun',
		dark: 'moon',
		system: 'laptop',
	} as const

	return (
		<ThemeSwitch
			userPreference={mode}
			className="group w-full"
			buttonClassName="w-full flex items-center text-left"
		>
			<Icon
				name={modeIcon[mode]}
				className="group-hover:animate-wiggle mr-2 text-body-md transition-transform"
			>
				{mode === 'system'
					? 'System Theme'
					: `${mode.charAt(0).toUpperCase() + mode.slice(1)} Theme`}
			</Icon>
		</ThemeSwitch>
	)
}
